import { useState } from 'react';
import { Button, Icon, Stack } from '@mui/material';
import { ExportColumn, ExportService, ExportValue } from '../../services/export/exportService';
import { LoadingService } from '../../services/loading/loadingService';

interface ExportButtonsProps<T> {
  title: string; fileName: string; columns: ExportColumn<T>[]; rows: T[]; disabled?: boolean;
  totalCount?: number; pageSize?: number; loadPage?: (page: number) => Promise<T[] | Error>;
  footer?: ExportValue[];
}

type ExportFormat = 'pdf' | 'xlsx' | 'csv';

export const ExportButtons = <T,>({ title, fileName, columns, rows, disabled, totalCount = rows.length, pageSize = 10, loadPage, footer }: ExportButtonsProps<T>) => {
  const [isExporting, setIsExporting] = useState(false);
  const isDisabled = disabled || isExporting || totalCount === 0;
  const loadAllRows = async () => {
    if (!loadPage || totalCount <= rows.length) return rows;
    const allRows: T[] = [];
    const pageCount = Math.ceil(totalCount / pageSize);
    for (let page = 1; page <= pageCount; page += 1) {
      const result = await loadPage(page);
      if (result instanceof Error) throw result;
      allRows.push(...result);
    }
    return allRows;
  };
  const handleExport = async (format: ExportFormat) => {
    setIsExporting(true);
    LoadingService.start();
    try {
      const exportRows = await loadAllRows();
      const options = { title, fileName, columns, rows: exportRows, footer };
      if (format === 'pdf') ExportService.exportPdf(options);
      if (format === 'xlsx') ExportService.exportXlsx(options);
      if (format === 'csv') ExportService.exportCsv(options);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível carregar todos os registros para exportação.');
    } finally { LoadingService.stop(); setIsExporting(false); }
  };
  return (
    <Stack direction="row" spacing={1}>
      <Button variant="outlined" size="small" disabled={isDisabled} onClick={() => handleExport('pdf')} startIcon={<Icon>picture_as_pdf</Icon>}>PDF</Button>
      <Button variant="outlined" size="small" disabled={isDisabled} onClick={() => handleExport('xlsx')} startIcon={<Icon>table_view</Icon>}>Excel</Button>
      <Button variant="outlined" size="small" disabled={isDisabled} onClick={() => handleExport('csv')} startIcon={<Icon>description</Icon>}>CSV</Button>
    </Stack>
  );
};
